import { notification } from "antd";
import cuid from "cuid";
import {
  filter,
  find,
  get,
  isArray,
  isEmpty,
  isNil,
  isObject,
  last,
  sortBy,
} from "lodash";
import moment from "moment";
import omitDeep from "omit-deep-lodash";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { HiOutlineExclamationCircle } from "react-icons/hi";
import { IoCheckmarkCircleOutline } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import { useLazyQuery, useMutation } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import { COLORS } from "shared/style/colors";
import format from "string-template";
import { GENERATE_QUOTE_DOCUMENTS } from "utils/api/graphql/mutations/attachment";
import { BUILD_GENERAL_COSTS } from "utils/api/graphql/mutations/costs";
import { DOWNLOAD_SINGLE_DOCUMENT } from "utils/api/graphql/mutations/download-document";
import { CREATE_LINK } from "utils/api/graphql/mutations/link";
import {
  DUPLICATE_PROJECT,
  UPDATE_PROJECT,
} from "utils/api/graphql/mutations/projects";
import {
  GENERATE_EXTERNAL_QUOTE,
  SEND_B2C_QUOTE,
} from "utils/api/graphql/mutations/quotes";
import { CREATE_SUBSCRIPTION } from "utils/api/graphql/mutations/subscription";
import { GENERAL_COSTS } from "utils/api/graphql/queries/general-cost";
import { INSURANCE_PLANS } from "utils/api/graphql/queries/insurance-plans";
import { OFFER } from "utils/api/graphql/queries/offers";
import { PROJECT } from "utils/api/graphql/queries/projects";
import {
  ACTION_TYPES,
  CONTACT_POINT_SYSTEM,
  DATE_FORMAT,
  INSURANCE_PLAN_PARAMS_RULES,
  KEYS,
  PROCESS_TYPES,
  RELATIONSHIP,
  SCREENS,
  TEMPLATES,
  TEMPLATE_TYPES,
} from "utils/constants";
import { getMinDeltaStart } from "utils/helpers/array";
import { generateSQLCondition } from "utils/helpers/graphql";
import { filterByConditions } from "../../../..";
import Loader from "../Components/Loader";
import Navbar from "../Components/Navbar/Navbar";
import NotEligible from "../NotEligible";
import View from "./View";

const findStatus = (data, template) => {
  return find(data, ({ template: _ }) => _ === template);
};

const Products = ({
  processId,
  tracer,
  project,
  onNext,
  onBack,
  currentStatus,
  setDisplayProject,
  territorial = false,
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refetch, setRefetch] = useState(false);
  const [refetchLoading, setRefetchLoading] = useState(false);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [buildGeneralCosts] = useMutation(BUILD_GENERAL_COSTS);
  const [updateProject] = useMutation(UPDATE_PROJECT);
  const [addSubscription] = useMutation(CREATE_SUBSCRIPTION);
  const [generateQuoteDocuments] = useMutation(GENERATE_QUOTE_DOCUMENTS);
  const [generateExternalQuote] = useMutation(GENERATE_EXTERNAL_QUOTE);
  const [sendQuoteMutation] = useMutation(SEND_B2C_QUOTE);
  const [addLink] = useMutation(CREATE_LINK);

  const [generalCosts, { data }] = useLazyQuery(GENERAL_COSTS);
  const [getOffer] = useLazyQuery(OFFER);
  const [getDeltaStart] = useLazyQuery(INSURANCE_PLANS, {
    fetchPolicy: "no-cache",
    variables: {
      where: {
        isStandalone: true,
      },
    },
  });
  const { t } = useTranslation(NAME_SPACES.PUBLIC.PROCESSES);
  const TRANSLATION = t("DETAIL", { returnObjects: true });
  const { t: tErrors } = useTranslation(NAME_SPACES.ERRORS);
  const API_ERRORS = tErrors("API", { returnObjects: true });
  const [downloadSingleDocument] = useMutation(DOWNLOAD_SINGLE_DOCUMENT);
  const [duplicateProject] = useMutation(DUPLICATE_PROJECT);

  const duplicateProjectOnLocked = () => {
    const { insurancePlans, currentSubscription, nextQuoteStatus, ...fields } =
      project?.fields;

    const Template =
      TEMPLATES[PROCESS_TYPES.B2C].THEMES[TEMPLATE_TYPES.RADIANCE_COSA]
        .PRODUCTS;
    const status = findStatus(
      get(tracer, "flow.process.processStatuses", []),
      Template,
    );
    const actionsToExecute = filterByConditions(
      get(status, "actions", []),
      project,
    );
    const syncActions = actionsToExecute
      .filter(({ type }) => type === ACTION_TYPES.SYNC_PROCESSES)
      .map(({ args: { status } }) => ({
        id: cuid(),
        status: { id: status },
      }));
    duplicateProject({
      variables: {
        where: { id: project.id },
        data: {
          statuses: [{ id: cuid(), status: { id: status.id } }, ...syncActions],
          fields,
        },
      },
      onCompleted: ({ duplicateProject }) => {
        localStorage.setItem(KEYS.PROJECT_ID, duplicateProject?.id);
        navigate(
          `${SCREENS.PRIVATE.GUEST.PROJECTS.DETAIL.path}/${duplicateProject?.id}`,
          {
            state: {
              process: get(omitDeep(tracer, "__typename"), "flow.process"),
              tracer: omitDeep(tracer, "__typename"),
            },
          },
        );
      },
    });
  };
  const [getProject] = useLazyQuery(PROJECT, {
    fetchPolicy: "no-cache",
    variables: { where: { id: project.id } },
  });

  const onSendQuote = async (insurancePlanId) => {
    const {
      data: { project },
    } = await getProject();
    const initialInsurancePlans = get(project, "fields.insurancePlans", []);
    if (initialInsurancePlans.includes(insurancePlanId)) {
      return onNext({
        payload: {
          fields: {
            ...project.fields,
            currentSubscription: get(project, "subscriptions", []).find(
              ({ insurancePlan }) => insurancePlan.id === insurancePlanId,
            )?.id,
          },
        },
      });
    }

    const status = get(
      last(
        sortBy(
          filter(
            project.statuses,
            ({ status }) => status.process.id === processId,
          ),
          "createdDate",
        ),
      ),
      "status",
    );
    const { possibleStatuses } = status;
    const [possibleStatus] = filterByConditions(
      sortBy(possibleStatuses, "order"),
      {
        ...project,
        status,
      },
    );

    addSubscription({
      variables: {
        data: {
          id: cuid(),
          project: { id: project.id },
          insurancePlan: {
            id: insurancePlanId,
          },
        },
      },
      onCompleted: ({
        addSubscription: { id: subscriptionId, insurancePlan },
      }) => {
        onNext({
          payload: {
            locked: false,
            fields: {
              ...project.fields,
              insurancePlans: [
                ...get(project, "fields.insurancePlans", []),
                insurancePlanId,
              ],
              selectedToCompare: [
                ...get(project, "fields.selectedToCompare", []),
                insurancePlanId,
              ],
              ...(!isNil(possibleStatus?.nextStatus) && {
                nextQuoteStatus: possibleStatus.nextStatus,
              }),
              currentSubscription: subscriptionId,
            },
          },
          onCompleted: () => {
            const source = localStorage.getItem(KEYS.SOURCE);
            addLink({
              variables: {
                data: {
                  url: get(
                    insurancePlan,
                    "config.generatedUrl",
                    `${process.env.REACT_APP_BASE_URL}/#/public/process/init/${source}`,
                  ),
                  project: { id: project.id },
                  subscription: { id: subscriptionId },
                  fields: {
                    process: { id: processId },
                    status: { id: get(possibleStatus, "nextStatus") },
                  },
                },
              },
              onCompleted: () => {
                if (project.visible || !get(project, "fields.shouldBeHidden"))
                  generateQuoteDocuments({
                    variables: {
                      data: {
                        project: { id: project.id },
                        insurancePlans: [{ id: insurancePlanId }],
                      },
                    },
                    onCompleted: () => {
                      generateExternalQuote({
                        variables: {
                          data: {
                            project: { id: project.id },
                            insurancePlans: [{ id: insurancePlanId }],
                          },
                        },
                        onCompleted: () => {
                          sendQuoteMutation({
                            variables: {
                              data: {
                                link: addLink?.id,
                                project: { id: project.id },
                                insurancePlans: [{ id: insurancePlanId }],
                              },
                            },
                            onCompleted: () =>
                              notification.open({
                                message: "Devis envoyé",
                                duration: 5,
                                icon: (
                                  <IoCheckmarkCircleOutline
                                    color={COLORS.C_SUCCESS}
                                  />
                                ),
                              }),
                          });
                        },
                        onError: () => {
                          notification.error({
                            message: API_ERRORS.GENERIC,
                          });
                        },
                      });
                    },
                  });
              },
            });
          },
        });
      },
    });
  };

  const onQuoteSubmit = async (insurancePlanId) => {
    const {
      data: { project },
    } = await getProject();

    const status = get(
      last(
        sortBy(
          filter(
            project.statuses,
            ({ status }) => status.process.id === processId,
          ),
          "createdDate",
        ),
      ),
      "status",
    );
    const { possibleStatuses } = status;
    const [possibleStatus] = filterByConditions(
      sortBy(possibleStatuses, "order"),
      {
        ...project,
        status,
      },
    );
    setQuoteLoading(true);
    addSubscription({
      variables: {
        data: {
          id: cuid(),
          project: { id: project.id },
          insurancePlan: {
            id: insurancePlanId,
          },
        },
      },
      onCompleted: ({
        addSubscription: { id: subscriptionId, insurancePlan },
      }) => {
        const source = localStorage.getItem(KEYS.SOURCE);
        addLink({
          variables: {
            data: {
              url: get(
                insurancePlan,
                "config.generatedUrl",
                `${process.env.REACT_APP_BASE_URL}/#/public/process/init/${source}`,
              ),
              project: { id: project.id },
              subscription: { id: subscriptionId },
              fields: {
                process: { id: processId },
                status: { id: get(possibleStatus, "nextStatus") },
              },
            },
          },
          onCompleted: () => {
            if (project.visible || !get(project, "fields.shouldBeHidden"))
              generateQuoteDocuments({
                variables: {
                  data: {
                    project: { id: project.id },
                    insurancePlans: [{ id: insurancePlanId }],
                  },
                },
                onCompleted: () => {
                  generateExternalQuote({
                    variables: {
                      data: {
                        project: { id: project.id },
                        insurancePlans: [{ id: insurancePlanId }],
                      },
                    },
                    onCompleted: () => {
                      sendQuoteMutation({
                        variables: {
                          data: {
                            link: addLink?.id,
                            project: { id: project.id },
                            insurancePlans: [{ id: insurancePlanId }],
                          },
                        },
                        onCompleted: () => {
                          setQuoteLoading(false);
                          notification.open({
                            message: "Devis envoyé",
                            duration: 5,
                            icon: (
                              <IoCheckmarkCircleOutline
                                color={COLORS.C_SUCCESS}
                              />
                            ),
                          });
                        },
                      });
                    },
                    onError: () => {
                      setQuoteLoading(false);
                      notification.error({
                        message: API_ERRORS.GENERIC,
                      });
                    },
                  });
                },
              });
          },
        });
      },
    });
  };

  useEffect(() => {
    getDeltaStart({
      onCompleted: ({ insurancePlans: { data: insurancePlans } }) => {
        const deltaRia = getMinDeltaStart({ insurancePlans, minValue: 30 });
        const deltaStart = getMinDeltaStart({
          insurancePlans,
          minValue: 1,
          key: INSURANCE_PLAN_PARAMS_RULES.DELTA_START,
        });
        const deltaS = project.ria ? deltaRia : deltaStart;
        if (project?.expired) {
          notification.open({
            message: format(TRANSLATION.EXPIRATION.TITLE, {
              issuanceDate: moment().add(deltaS, "days").format(DATE_FORMAT),
            }),
            description: TRANSLATION.EXPIRATION.DESCRIPTION,
            duration: 5,
            icon: <HiOutlineExclamationCircle size={32} />,
            type: "error",
          });
        }
        const issuanceDate = moment().add(deltaS, "days");

        updateProject({
          variables: {
            where: { id: project.id },
            data: {
              expired: false,
              contract: {
                ...project.contract,
                issuanceDate,
              },
            },
          },
          onCompleted: ({ updateProject }) => {
            const updatedProject = omitDeep(updateProject, "__typename");
            setDisplayProject(updatedProject);
            const adherentAge = moment(
              get(project, "contact.user.birthDate"),
            ).startOf("year");
            const profession = get(project, "contact.profession");

            const age = moment().diff(adherentAge, "years");

            const profile = get(project, "contact.fields.profile");
            const zipCode = get(
              get(project, "contact.telecoms", []).find(
                ({ system }) => system === CONTACT_POINT_SYSTEM.ZIP_CODE,
              ),
              "value",
              "",
            ).toString();

            const department = zipCode.substring(0, 2);

            const municipality = get(
              get(project, "contact.telecoms", []).find(
                ({ system }) => system === CONTACT_POINT_SYSTEM.CITY,
              ),
              "value",
              "",
            )
              .toString()
              .replace(/'/g, "''");

            const sqlConditions = [
              generateSQLCondition(
                INSURANCE_PLAN_PARAMS_RULES.MIN_AGE,
                "r.value IS NULL OR CAST(r.value AS NUMERIC) <= :value",
                age,
              ),
              generateSQLCondition(
                INSURANCE_PLAN_PARAMS_RULES.MAX_AGE,
                "r.value IS NULL OR CAST(r.value AS NUMERIC) >= :value",
                age,
              ),
              generateSQLCondition(
                INSURANCE_PLAN_PARAMS_RULES.PROCESSES,
                `r.value IS NULL OR r.value @> to_jsonb(ARRAY[:value])::jsonb OR jsonb_array_length(r.value) = 0`,
                `'${processId}'`,
              ),
              profile &&
                generateSQLCondition(
                  INSURANCE_PLAN_PARAMS_RULES.PROFILES,
                  `r.value IS NULL OR r.value @> to_jsonb(ARRAY[:value])::jsonb OR jsonb_array_length(r.value) = 0`,
                  `'${profile}'`,
                ),
              generateSQLCondition(
                INSURANCE_PLAN_PARAMS_RULES.DEPARTMENTS,
                `r.value IS NULL OR r.value @> to_jsonb(ARRAY[:value])::jsonb OR jsonb_array_length(r.value) = 0`,
                `'${department}'`,
              ),
              generateSQLCondition(
                INSURANCE_PLAN_PARAMS_RULES.ZIP_CODES,
                `r.value IS NULL OR r.value @> to_jsonb(ARRAY[:value])::jsonb OR jsonb_array_length(r.value) = 0`,
                `'${zipCode}'`,
              ),
              generateSQLCondition(
                INSURANCE_PLAN_PARAMS_RULES.MUNICIPALITIES,
                `r.value IS NULL OR r.value @> to_jsonb(ARRAY[:value])::jsonb OR jsonb_array_length(r.value) = 0`,
                `'${municipality}'`,
              ),
            ].filter(Boolean);

            const insurancePlanFilter = {
              where: {
                isStandalone: true,
              },
              raw: sqlConditions,
              custom: {
                AND: [
                  {
                    OR: [
                      { startDate: null },
                      {
                        startDate: issuanceDate,
                        operation: "<=",
                      },
                    ],
                  },
                  {
                    OR: [
                      { endDate: null },
                      {
                        endDate: issuanceDate,
                        operation: ">=",
                      },
                    ],
                  },
                ],
              },
            };
            buildGeneralCosts({
              variables: {
                data: {
                  project: { id: project.id },
                  insurancePlanFilter,
                },
              },
              onCompleted: ({ buildGeneralCosts }) => {
                const ids = buildGeneralCosts
                  .filter(({ cost }) => cost)
                  .map(({ id }) => id);
                generalCosts({
                  fetchPolicy: "no-cache",
                  variables: {
                    isIn: { id: ids },
                  },
                  onCompleted: ({ generalCosts }) => {
                    const productsData = omitDeep(
                      get(generalCosts, "data", []),
                      "__typename",
                    );

                    const [recommendedProduct] = productsData.splice(0, 1);
                    if (recommendedProduct)
                      productsData.splice(1, 0, recommendedProduct);
                    setProducts(productsData);
                    setLoading(false);
                    setRefetchLoading(false);
                  },
                });
              },
            });
          },
        });
      },
    });
  }, [refetch]);

  const refetchCosts = ({ contact }) => {
    setRefetchLoading(true);
    const relatedPersons = get(contact, "relatedPersons", []).filter((_) => _);
    const hasChildren = !!relatedPersons.filter(
      ({ relationship }) => relationship === RELATIONSHIP.CHILD,
    ).length;
    const hasSpouse = !isEmpty(
      relatedPersons.find(
        ({ relationship }) => relationship === RELATIONSHIP.SPOUSE,
      ),
    );
    const effectiveDate = get(project, "contract.issuanceDate");
    const children = relatedPersons.filter(
      ({ relationship }) => relationship === RELATIONSHIP.CHILD,
    );
    const hasChildOver20 = children.some((child) => {
      const birthDate = isObject(get(child, "identity.user.birthDate"))
        ? get(child, "identity.user.birthDate").toISOString()
        : get(child, "identity.user.birthDate");
      if (!birthDate) return false;
      const age = moment(effectiveDate).diff(birthDate, "years");
      return age >= 20;
    });
    updateProject({
      variables: {
        where: { id: project.id },
        data: {
          contact,
          fields: {
            ...get(project, "fields"),
            hasRelatedPersons: {
              hasChildren,
              hasSpouse,
              hasChildOver20,
            },
          },
        },
      },
      onCompleted: () => setRefetch((prev) => !prev),
    });
  };
  if (loading || !isArray(products)) return <Loader />;
  return (
    <>
      <Navbar
        project={project}
        progress={currentStatus?.progress}
        tracer={tracer}
        showProfile
      />
      {products.length === 0 ? (
        <NotEligible
          project={project}
          onNext={onNext}
          onBack={onBack}
          currentStatus={currentStatus}
          tracer={tracer}
        />
      ) : (
        <View
          products={products}
          count={get(data, "generalCosts.count")}
          onBack={onBack}
          onSubmit={onSendQuote}
          disabledProducts={get(project, "fields.insurancePlans", [])}
          contact={get(project, "contact", {})}
          refetchCosts={refetchCosts}
          downloadSingleDocument={downloadSingleDocument}
          locked={project.locked}
          duplicateProject={duplicateProjectOnLocked}
          refetchLoading={refetchLoading}
          quoteLoading={quoteLoading}
          onQuoteSubmit={onQuoteSubmit}
          territorial={territorial}
        />
      )}
    </>
  );
};

export default Products;
